// AssignmentsPage.tsx
import React, { useState, useEffect } from "react";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Input, Select, Textarea } from "../../components/ui/FormFields";
import { db, auth } from "../../firebase/config";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch
} from "firebase/firestore";
import { examAssignmentService } from "../../services/examAssignmentService";
import Swal from "sweetalert2";

interface Assignment {
  id: string;
  examId: string;
  candidateId: string;
  orgId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'expired';
  assignedAt: string;
  startTime: string | null;
  endTime: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  startedAt: string | null;
  completedAt: string | null;
  score: number | null;
  maxScore: number | null;
  timeSpent: number | null;
  createdBy: string;
  notes: string | null;
}

interface Exam {
  id: string;
  name: string;
  description: string;
  instructions?: string;
  isActive: boolean;
  organizationId: string;
  organizationName?: string;
  createdAt?: string;
  createdBy?: string;
  totalQuestions?: number;
  totalDuration?: number;
}

interface Candidate {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  orgId?: string | null;
}

interface Group {
  id: string;
  name: string;
  description: string;
  orgId: string;
  candidateIds: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  candidateCount?: number;
}

interface AssignmentWithDetails extends Assignment {
  examName?: string;
  candidateName?: string;
  candidateEmail?: string;
  assignmentType?: 'single' | 'group';
  groupName?: string;
}

interface Props {
  orgId?: string;
}

const BLANK_ASSIGNMENT = {
  examId: "",
  candidateIds: [] as string[],
  groupId: "",
  scheduledStart: "",
  scheduledEnd: "",
  notes: ""
};

const AssignmentsPage: React.FC<Props> = ({ orgId: propOrgId }) => {
  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAssignments, setViewingAssignments] = useState<AssignmentWithDetails[]>([]);
  const [viewingExamName, setViewingExamName] = useState("");
  const [editAssignment, setEditAssignment] = useState<AssignmentWithDetails | null>(null);
  const [deleteAssignment, setDeleteAssignment] = useState<AssignmentWithDetails | null>(null);
  const [form, setForm] = useState(BLANK_ASSIGNMENT);
  const [statusFilter, setStatusFilter] = useState("all");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState<Set<string>>(new Set());
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [assignmentType, setAssignmentType] = useState<"single" | "bulk">("single");

  // Get org ID from localStorage
  const getOrgId = (): string | null => {
    if (propOrgId) return propOrgId;

    try {
      const storedUser = localStorage.getItem("currentUser");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.organizationId) {
          console.log("Found organizationId in localStorage:", user.organizationId);
          return user.organizationId;
        }
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }

    return currentOrgId;
  };

  const effectiveOrgId = getOrgId();

  // Load data on mount
  useEffect(() => {
    loadOrgId();
  }, []);

  useEffect(() => {
    if (effectiveOrgId) {
      console.log("Loading data for org ID:", effectiveOrgId);
      loadAllData();
    } else {
      console.log("Waiting for org ID to load...");
      if (!propOrgId && !currentOrgId) {
        loadOrgId();
      } else if (!effectiveOrgId) {
        setLoading(false);
      }
    }
  }, [effectiveOrgId]);

  const loadOrgId = async () => {
    try {
      const storedUser = localStorage.getItem("currentUser");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.organizationId) {
          console.log("Loaded org ID from localStorage:", user.organizationId);
          setCurrentOrgId(user.organizationId);
          return;
        }
      }

      const currentUser = auth.currentUser;
      if (currentUser) {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("authUid", "==", currentUser.uid));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const userData = snapshot.docs[0].data();
          if (userData.orgId) {
            console.log("Loaded org ID from Firestore:", userData.orgId);
            setCurrentOrgId(userData.orgId);
            return;
          }
        }
      }

      console.warn("No org ID found in localStorage or Firestore");
    } catch (error) {
      console.error("Error loading org ID:", error);
    }
  };

  const loadAllData = async () => {
    if (!effectiveOrgId) {
      console.error("Cannot load data: No org ID available");
      setLoading(false);
      return;
    }

    console.log("loadAllData started with orgId:", effectiveOrgId);
    setLoading(true);

    try {
      await Promise.all([
        loadExamsData(),
        loadCandidatesData(),
        loadGroupsData(),
        loadAssignmentsData()
      ]);
    } catch (error) {
      console.error("Error loading data:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load data'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadExamsData = async () => {
    if (!effectiveOrgId) return;

    console.log("loadExamsData called with organizationId:", effectiveOrgId);
    try {
      const examsRef = collection(db, "assessmentTemplates");
      const q = query(examsRef, where("organizationId", "==", effectiveOrgId));
      const snapshot = await getDocs(q);
      console.log("Exams snapshot size:", snapshot.size);

      const loadedExams = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || "Untitled Exam",
          description: data.description || "",
          instructions: data.instructions || "",
          isActive: data.isActive === true,
          organizationId: data.organizationId,
          organizationName: data.organizationName,
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          totalQuestions: data.totalQuestions || 0,
          totalDuration: data.totalDuration || 0
        } as Exam;
      });

      setExams(loadedExams.filter(exam => exam.isActive !== false));
      console.log("Loaded exams:", loadedExams.length);
    } catch (error) {
      console.error("Error loading exams:", error);
    }
  };

  const loadCandidatesData = async () => {
    if (!effectiveOrgId) return;

    console.log("loadCandidatesData called with orgId:", effectiveOrgId);
    try {
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("orgId", "==", effectiveOrgId),
        where("role", "==", "candidate")
      );
      const snapshot = await getDocs(q);
      console.log("Candidates snapshot size:", snapshot.size);

      const loadedCandidates = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Candidate[];

      setCandidates(loadedCandidates.filter(c => c.status === "active"));
      console.log("Loaded candidates:", loadedCandidates.length);
    } catch (error) {
      console.error("Error loading candidates:", error);
    }
  };

  const loadGroupsData = async () => {
    if (!effectiveOrgId) return;

    console.log("loadGroupsData called with orgId:", effectiveOrgId);
    try {
      const groupsRef = collection(db, "groups");
      const q = query(groupsRef, where("orgId", "==", effectiveOrgId));
      const snapshot = await getDocs(q);
      console.log("Groups snapshot size:", snapshot.size);

      const loadedGroups = snapshot.docs.map(doc => {
        const group = { id: doc.id, ...doc.data() } as Group;
        group.candidateCount = group.candidateIds?.length || 0;
        return group;
      });

      setGroups(loadedGroups.sort((a, b) => a.name.localeCompare(b.name)));
      console.log("Loaded groups:", loadedGroups.length);
    } catch (error) {
      console.error("Error loading groups:", error);
    }
  };

  const loadAssignmentsData = async () => {
    if (!effectiveOrgId) return;

    console.log("loadAssignmentsData called with orgId:", effectiveOrgId);
    try {
      const loadedAssignments = await examAssignmentService.getAssignmentsByOrg(effectiveOrgId);
      console.log("Assignments from service:", loadedAssignments.length);
      
      // Enrich assignments with exam and candidate details
      const enrichedAssignments = loadedAssignments.map(assignment => {
        const exam = exams.find(e => e.id === assignment.examId);
        const candidate = candidates.find(c => c.id === assignment.candidateId);
        
        // Determine if this assignment was from a group (check notes or if multiple candidates with same exam)
        const isGroupAssignment = assignment.notes?.includes("Bulk assignment to group") || false;
        
        return {
          ...assignment,
          examName: exam?.name || "Unknown Exam",
          candidateName: candidate?.name || "Unknown Candidate",
          candidateEmail: candidate?.email || "",
          assignmentType: isGroupAssignment ? "group" as const : "single" as const,
          groupName: isGroupAssignment ? assignment.notes?.replace("Bulk assignment to group: ", "") || "" : ""
        };
      });
      
      setAssignments(enrichedAssignments);
      setSelectedAssignments(new Set());
    } catch (error) {
      console.error("Error loading assignments:", error);
    }
  };

  const getCandidatesFromGroup = (groupId: string): Candidate[] => {
    const group = groups.find(g => g.id === groupId);
    if (!group || !group.candidateIds) return [];
    return candidates.filter(c => group.candidateIds.includes(c.id));
  };

  const handleSaveAssignment = async () => {
    if (!form.examId) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select an exam'
      });
      return;
    }

    let candidateIdsToAssign: string[] = [];

    // For edit mode
    if (editAssignment) {
      candidateIdsToAssign = form.candidateIds;
    }
    // For new assignments
    else {
      if (assignmentType === "single") {
        if (form.candidateIds.length === 0) {
          await Swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: 'Please select at least one candidate'
          });
          return;
        }
        candidateIdsToAssign = form.candidateIds;
      } else if (assignmentType === "bulk") {
        if (!form.groupId) {
          await Swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: 'Please select a group'
          });
          return;
        }
        const groupCandidates = getCandidatesFromGroup(form.groupId);
        if (groupCandidates.length === 0) {
          await Swal.fire({
            icon: 'warning',
            title: 'No Candidates',
            text: 'This group has no candidates. Please add candidates to the group first.'
          });
          return;
        }
        candidateIdsToAssign = groupCandidates.map(c => c.id);
      }
    }

    if (!effectiveOrgId) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No organization found. Please refresh the page.'
      });
      return;
    }

    if (candidateIdsToAssign.length === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'No candidates selected. Please select at least one candidate.'
      });
      return;
    }

    setLoading(true);
    setLoadingMessage(`Assigning exam to ${candidateIdsToAssign.length} candidate(s)...`);

    try {
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid || "system";
      
      let notes = form.notes || undefined;
      if (assignmentType === "bulk" && form.groupId) {
        const group = groups.find(g => g.id === form.groupId);
        notes = `Bulk assignment to group: ${group?.name}`;
      }

      const result = await examAssignmentService.bulkCreateAssignments({
        examId: form.examId,
        candidateIds: candidateIdsToAssign,
        orgId: effectiveOrgId,
        createdBy: userId,
        scheduledStart: form.scheduledStart || undefined,
        scheduledEnd: form.scheduledEnd || undefined,
        notes: notes
      });

      if (result.success.length > 0) {
        await Swal.fire({
          icon: 'success',
          title: 'Assign Complete!',
          html: `
            <div class="text-left">
              <p><strong>Successfully Assigned:</strong> ${result.success.length}</p>
              <p><strong>Failed:</strong> ${result.failed.length}</p>
            </div>
          `
        });

        await loadAssignmentsData();
        setShowModal(false);
        resetForm();
      } else {
        throw new Error("No assignments were created. Please make sure you have selected candidates.");
      }
    } catch (error: any) {
      console.error("Error saving assignment:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to create assignments"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAssignment = async () => {
    if (!editAssignment || !form.examId || !form.candidateIds[0]) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select exam and candidate'
      });
      return;
    }

    setLoading(true);
    setLoadingMessage("Updating assignment...");

    try {
      await examAssignmentService.updateAssignment(editAssignment.id, {
        examId: form.examId,
        candidateId: form.candidateIds[0],
        scheduledStart: form.scheduledStart || null,
        scheduledEnd: form.scheduledEnd || null,
        notes: form.notes || null
      });

      await Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Assignment updated successfully',
        timer: 1500,
        showConfirmButton: false
      });

      await loadAssignmentsData();
      setShowModal(false);
      resetForm();
    } catch (error: any) {
      console.error("Error updating assignment:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to update assignment"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewExamAssignments = (examId: string, examName: string) => {
    const examAssignments = assignments.filter(a => a.examId === examId);
    setViewingExamName(examName);
    setViewingAssignments(examAssignments);
    setShowViewModal(true);
  };

  const handleDeleteAssignment = async () => {
    if (!deleteAssignment) return;

    setLoading(true);
    setLoadingMessage("Deleting assignment...");

    try {
      await examAssignmentService.deleteAssignment(deleteAssignment.id);
      await loadAssignmentsData();
      setDeleteAssignment(null);

      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Assignment deleted successfully',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error: any) {
      console.error("Error deleting assignment:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to delete assignment"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedAssignments.size === 0) return;

    const result = await Swal.fire({
      title: 'Delete Selected Assignments?',
      text: `Delete ${selectedAssignments.size} assignments? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, delete all',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setBulkDeleting(true);
    setLoading(true);
    setLoadingMessage(`Deleting ${selectedAssignments.size} assignments...`);

    try {
      const ids = Array.from(selectedAssignments);
      const deleteResult = await examAssignmentService.bulkDeleteAssignments(ids);
      await loadAssignmentsData();

      if (deleteResult.success > 0) {
        await Swal.fire({
          icon: 'success',
          title: 'Bulk Delete Complete!',
          text: `Successfully deleted ${deleteResult.success} assignments`,
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error: any) {
      console.error("Error bulk deleting:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || "Failed to delete assignments"
      });
    } finally {
      setBulkDeleting(false);
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedAssignments(new Set());
    } else {
      const allIds = filteredAssignments.map(a => a.id);
      setSelectedAssignments(new Set(allIds));
    }
  };

  const handleSelectAssignment = (id: string) => {
    setSelectedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCandidateSelection = (candidateId: string) => {
    setForm(prev => {
      const newIds = prev.candidateIds.includes(candidateId)
        ? prev.candidateIds.filter(id => id !== candidateId)
        : [...prev.candidateIds, candidateId];
      return { ...prev, candidateIds: newIds };
    });
  };

  const handleSelectAllCandidates = () => {
    if (form.candidateIds.length === candidates.length) {
      setForm(prev => ({ ...prev, candidateIds: [] }));
    } else {
      setForm(prev => ({ ...prev, candidateIds: candidates.map(c => c.id) }));
    }
  };

  const resetForm = () => {
    setForm(BLANK_ASSIGNMENT);
    setEditAssignment(null);
    setAssignmentType("single");
  };

  const openNew = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (assignment: AssignmentWithDetails) => {
    setEditAssignment(assignment);
    setForm({
      examId: assignment.examId,
      candidateIds: [assignment.candidateId],
      groupId: "",
      scheduledStart: assignment.scheduledStart || "",
      scheduledEnd: assignment.scheduledEnd || "",
      notes: assignment.notes || ""
    });
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { text: string; variant: any }> = {
      pending: { text: "Pending", variant: "warning" },
      in_progress: { text: "In Progress", variant: "info" },
      completed: { text: "Completed", variant: "success" },
      expired: { text: "Expired", variant: "danger" }
    };
    const config = variants[status] || { text: status, variant: "default" };
    return <Badge text={config.text} variant={config.variant} />;
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = 
      (assignment.candidateName?.toLowerCase().includes(search.toLowerCase()) || false) ||
      (assignment.examName?.toLowerCase().includes(search.toLowerCase()) || false);
    const matchesStatus = statusFilter === "all" || assignment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const selectedFilteredCount = filteredAssignments.filter(a => selectedAssignments.has(a.id)).length;
  const isAllSelected = filteredAssignments.length > 0 && selectedFilteredCount === filteredAssignments.length;
  const isSomeSelected = selectedFilteredCount > 0 && selectedFilteredCount < filteredAssignments.length;

  // Group assignments by exam for the view modal
  const assignmentsByExam = exams.reduce((acc, exam) => {
    const examAssignments = assignments.filter(a => a.examId === exam.id);
    if (examAssignments.length > 0) {
      acc.push({
        examId: exam.id,
        examName: exam.name,
        count: examAssignments.length,
        assignments: examAssignments
      });
    }
    return acc;
  }, [] as { examId: string; examName: string; count: number; assignments: AssignmentWithDetails[] }[]);

  const stats = {
    total: assignments.length,
    pending: assignments.filter(a => a.status === "pending").length,
    inProgress: assignments.filter(a => a.status === "in_progress").length,
    completed: assignments.filter(a => a.status === "completed").length,
    expired: assignments.filter(a => a.status === "expired").length
  };

  const columns: Column<AssignmentWithDetails>[] = [
    {
      key: "select",
      label: "",
      render: (_, r) => (
        <input
          type="checkbox"
          checked={selectedAssignments.has(r.id)}
          onChange={() => handleSelectAssignment(r.id)}
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          disabled={bulkDeleting || loading}
        />
      )
    },
    {
      key: "examName",
      label: "Exam Name",
      render: (v, r) => (
        <div>
          <p className="font-semibold text-gray-800">{v}</p>
          <button
            onClick={() => handleViewExamAssignments(r.examId, v)}
            className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
          >
            View all ({assignments.filter(a => a.examId === r.examId).length})
          </button>
        </div>
      )
    },
    {
      key: "candidateName",
      label: "Candidate",
      render: (v, r) => (
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-300 to-purple-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {v?.charAt(0) || "?"}
          </div>
          <div>
            <p className="font-bold text-gray-800 text-sm">{v}</p>
            <p className="text-xs text-gray-400">{r.candidateEmail}</p>
          </div>
        </div>
      )
    },
    {
      key: "assignmentType",
      label: "Type",
      render: (v) => (
        <Badge 
          text={v === "group" ? "👥 Group" : "👤 Single"} 
          variant={v === "group" ? "info" : "default"} 
        />
      )
    },
    {
      key: "groupName",
      label: "Group",
      render: (v) => v ? <span className="text-xs text-gray-600">{v}</span> : <span className="text-xs text-gray-400">-</span>
    },
    {
      key: "scheduledStart",
      label: "Schedule",
      render: (v, r) => {
        if (v || r.scheduledEnd) {
          return (
            <div className="text-xs">
              {v && <div>From: {new Date(v).toLocaleDateString()}</div>}
              {r.scheduledEnd && <div>To: {new Date(r.scheduledEnd).toLocaleDateString()}</div>}
            </div>
          );
        }
        return <span className="text-xs text-gray-400">No schedule</span>;
      }
    },
    {
      key: "status",
      label: "Status",
      render: (v) => getStatusBadge(v)
    },
    {
      key: "assignedAt",
      label: "Assigned Date",
      render: (v) => new Date(v).toLocaleDateString()
    },
    {
      key: "score",
      label: "Score",
      render: (v, r) => {
        if (r.status === "completed" && r.score !== null && r.maxScore !== null) {
          const percentage = (r.score / r.maxScore) * 100;
          return (
            <div>
              <p className="font-semibold">{r.score}/{r.maxScore}</p>
              <p className="text-xs text-gray-500">{percentage.toFixed(1)}%</p>
            </div>
          );
        }
        return <span className="text-gray-400">-</span>;
      }
    },
    {
      key: "id",
      label: "Actions",
      render: (_, r) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEdit(r)}
            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
            disabled={loading || bulkDeleting}
          >
            Edit
          </button>
          <button
            onClick={() => setDeleteAssignment(r)}
            className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 transition-colors"
            disabled={loading || bulkDeleting}
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  // Show loading state while checking for org ID
  if (loading && !effectiveOrgId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organisation details...</p>
        </div>
      </div>
    );
  }

  if (!effectiveOrgId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-2">⚠️</div>
          <p className="text-gray-600 mb-2">No organisation found.</p>
          <p className="text-sm text-gray-500">Please ensure you are logged in and have an organisation assigned.</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900">{loadingMessage}</h3>
            </div>
          </div>
        </div>
      )}

      <PageShell
        title="Exam Assignments"
        subtitle="Assign exams to candidates, track progress, and manage all assignments"
        action={{ label: "New Assignment", onClick: openNew, disabled: loading || bulkDeleting }}
      >
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <StatCard label="Total Assignments" value={stats.total} icon="📋" color="#6366f1" />
          <StatCard label="Pending" value={stats.pending} icon="⏳" color="#f59e0b" />
          <StatCard label="In Progress" value={stats.inProgress} icon="🔄" color="#3b82f6" />
          <StatCard label="Completed" value={stats.completed} icon="✅" color="#10b981" />
          <StatCard label="Expired" value={stats.expired} icon="⏰" color="#ef4444" />
        </div>

        {/* Exams Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {assignmentsByExam.slice(0, 4).map(exam => (
            <GlassCard key={exam.examId} className="p-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleViewExamAssignments(exam.examId, exam.examName)}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{exam.examName}</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">{exam.count}</p>
                  <p className="text-xs text-gray-500">Total Assignments</p>
                </div>
                <div className="text-3xl">📝</div>
              </div>
            </GlassCard>
          ))}
        </div>

        <GlassCard className="p-5">
          {/* Filters and Actions */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Input
                value={search}
                onChange={setSearch}
                placeholder="🔍 Search by candidate or exam..."
                disabled={loading || bulkDeleting}
              />
            </div>
            <div className="w-48">
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "pending", label: "Pending" },
                  { value: "in_progress", label: "In Progress" },
                  { value: "completed", label: "Completed" },
                  { value: "expired", label: "Expired" }
                ]}
              />
            </div>
            {selectedAssignments.size > 0 && (
              <Button variant="danger" onClick={handleBulkDeleteSelected} disabled={bulkDeleting || loading}>
                {bulkDeleting ? "🗑️ Deleting..." : `🗑️ Delete Selected (${selectedAssignments.size})`}
              </Button>
            )}
          </div>

          {/* Select All Checkbox */}
          {filteredAssignments.length > 0 && (
            <div className="mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(el) => {
                  if (el) el.indeterminate = isSomeSelected;
                }}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                disabled={bulkDeleting}
              />
              <span className="text-sm font-medium text-gray-700">
                Select All ({filteredAssignments.length} assignments)
              </span>
              {selectedAssignments.size > 0 && (
                <span className="text-sm text-indigo-600 ml-2">
                  ({selectedAssignments.size} selected)
                </span>
              )}
            </div>
          )}

          {/* Table */}
          {filteredAssignments.length === 0 && !loading ? (
            <div className="text-center py-8 text-gray-400">
              {search || statusFilter !== "all"
                ? "No assignments match your filters"
                : candidates.length === 0
                  ? "No candidates available. Please add candidates first."
                  : exams.length === 0
                    ? "No exams available. Please create exams first."
                    : "No assignments yet. Click 'New Assignment' to get started."}
            </div>
          ) : (
            <Table columns={columns} data={filteredAssignments} />
          )}
        </GlassCard>
      </PageShell>

      {/* View Assignments Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={`Assignments for "${viewingExamName}"`}
        size="xl"
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Total: {viewingAssignments.length} candidate(s) assigned
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {viewingAssignments.map(assignment => (
                  <tr key={assignment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-300 to-purple-400 flex items-center justify-center text-white font-bold text-xs">
                          {assignment.candidateName?.charAt(0) || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{assignment.candidateName}</p>
                          <p className="text-xs text-gray-400">{assignment.candidateEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(assignment.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{new Date(assignment.assignedAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {assignment.scheduledStart || assignment.scheduledEnd ? (
                        <div className="text-xs">
                          {assignment.scheduledStart && <div>From: {new Date(assignment.scheduledStart).toLocaleDateString()}</div>}
                          {assignment.scheduledEnd && <div>To: {new Date(assignment.scheduledEnd).toLocaleDateString()}</div>}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No schedule</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {assignment.status === "completed" && assignment.score !== null ? (
                        <div>
                          <span className="font-semibold">{assignment.score}/{assignment.maxScore}</span>
                          <span className="text-xs text-gray-500 ml-1">
                            ({((assignment.score / (assignment.maxScore || 1)) * 100).toFixed(0)}%)
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowViewModal(false);
                            openEdit(assignment);
                          }}
                          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setShowViewModal(false);
                            setDeleteAssignment(assignment);
                          }}
                          className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {viewingAssignments.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              No candidates assigned to this exam yet.
            </div>
          )}
        </div>
      </Modal>

      {/* Assignment Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editAssignment ? "Edit Assignment" : "New Assignment"}
        size="xl"
      >
        <div className="space-y-5 max-h-[80vh] overflow-y-auto px-1">
          {/* Assignment Type Selection (only for new assignments) */}
          {!editAssignment && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Assignment Type</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={assignmentType === "single"}
                    onChange={() => {
                      setAssignmentType("single");
                      setForm(f => ({ ...f, groupId: "", candidateIds: [] }));
                    }}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <span className="text-sm">👤 Single / Multiple Candidates</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={assignmentType === "bulk"}
                    onChange={() => {
                      setAssignmentType("bulk");
                      setForm(f => ({ ...f, candidateIds: [], groupId: "" }));
                    }}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <span className="text-sm">👥 Assign to Group</span>
                </label>
              </div>
            </div>
          )}

          {/* Exam Selection */}
          <Select
            label="Select Exam"
            value={form.examId}
            onChange={(v) => setForm(f => ({ ...f, examId: v }))}
            options={[
              { value: "", label: "-- Select Exam --" },
              ...exams.map(exam => ({
                value: exam.id,
                label: `${exam.name} (${exam.totalQuestions || 0} Questions)`
              }))
            ]}
            required
            disabled={loading}
          />

          {/* Candidate/Group Selection based on type */}
          {!editAssignment && (
            <>
              {assignmentType === "single" ? (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Select Candidates ({form.candidateIds.length} selected)
                    </label>
                    {candidates.length > 0 && (
                      <button
                        type="button"
                        onClick={handleSelectAllCandidates}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        {form.candidateIds.length === candidates.length ? "Deselect All" : "Select All"}
                      </button>
                    )}
                  </div>
                  <div className="border rounded-lg p-3 max-h-64 overflow-y-auto bg-white">
                    {candidates.length === 0 ? (
                      <p className="text-center text-gray-400 py-4">No candidates available. Please add candidates first.</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {candidates.map(candidate => (
                          <label key={candidate.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.candidateIds.includes(candidate.id)}
                              onChange={() => handleCandidateSelection(candidate.id)}
                              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              disabled={loading}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 text-sm">{candidate.name}</p>
                              <p className="text-xs text-gray-500">{candidate.email}</p>
                            </div>
                            <Badge text={candidate.status || "active"} size="sm" />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {form.candidateIds.length === 0 && candidates.length > 0 && (
                    <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                      <span>⚠️</span> Please select at least one candidate
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <Select
                    label="Select Group"
                    value={form.groupId}
                    onChange={(v) => {
                      setForm(f => ({ ...f, groupId: v, candidateIds: [] }));
                    }}
                    options={[
                      { value: "", label: "-- Select Group --" },
                      ...groups.map(group => ({
                        value: group.id,
                        label: `${group.name} (${group.candidateCount || 0} candidates)`
                      }))
                    ]}
                    disabled={loading}
                  />
                  {form.groupId && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800 flex items-center gap-2">
                        <span>📌</span>
                        This will assign the exam to <strong>{getCandidatesFromGroup(form.groupId).length}</strong> candidate(s) in this group
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* For edit mode, show single candidate selection */}
          {editAssignment && (
            <Select
              label="Select Candidate"
              value={form.candidateIds[0] || ""}
              onChange={(v) => setForm(f => ({ ...f, candidateIds: [v] }))}
              options={[
                { value: "", label: "-- Select Candidate --" },
                ...candidates.map(candidate => ({
                  value: candidate.id,
                  label: `${candidate.name} (${candidate.email})`
                }))
              ]}
              required
              disabled={loading}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Scheduled Start (Optional)"
              type="datetime-local"
              value={form.scheduledStart}
              onChange={(v) => setForm(f => ({ ...f, scheduledStart: v }))}
              disabled={loading}
            />
            <Input
              label="Scheduled End (Optional)"
              type="datetime-local"
              value={form.scheduledEnd}
              onChange={(v) => setForm(f => ({ ...f, scheduledEnd: v }))}
              disabled={loading}
            />
          </div>

          <Textarea
            label="Notes (Optional)"
            value={form.notes}
            onChange={(v) => setForm(f => ({ ...f, notes: v }))}
            placeholder="Add any additional notes..."
            rows={3}
            disabled={loading}
          />

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={editAssignment ? handleUpdateAssignment : handleSaveAssignment} disabled={loading}>
              {loading ? "Processing..." : (editAssignment ? "Update Assignment" : "Create Assignment")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteAssignment}
        onClose={() => setDeleteAssignment(null)}
        onConfirm={handleDeleteAssignment}
        title="Delete Assignment"
        message={`Are you sure you want to delete the assignment for "${deleteAssignment?.candidateName}" on exam "${deleteAssignment?.examName}"?`}
        confirmLabel="Delete"
      />
    </>
  );
};

export default AssignmentsPage;